import { useMutation } from '@tanstack/react-query'
import { formatISO } from 'date-fns'
import { WeekStartsOn, User } from '@opallabs/concepts'
import {
  OpalApiRequestBody,
  OpalApiRequestParams,
  OpalApiResponseDocument,
} from '../../../schema'
import { views } from '../../../v3/views'

export type CreateViewMutationParams = {
  creator: User
  defaultTimescale?: 'day' | 'week' | 'month' | 'quarter'
  endDate: Date
  parentBlockId?: string
  startDate: Date
  startWeekdayIndex?: WeekStartsOn
  title: string
  workspaceUuid: string
}

type ViewResponseDocument = OpalApiResponseDocument<'CreateViewV3', 201>
type ViewRequestBody = OpalApiRequestBody<'CreateViewV3'>

type QueryParams = {
  include?: OpalApiRequestParams<'CreateViewV3'>['query']['include']
  onMutate?: (params: CreateViewMutationParams) => void
  onSuccess?: (
    data: ViewResponseDocument,
    params: CreateViewMutationParams
  ) => void
}

const buildRequestBody = ({
  creator,
  endDate,
  defaultTimescale,
  parentBlockId,
  startDate,
  startWeekdayIndex,
  title,
  workspaceUuid,
}: CreateViewMutationParams): ViewRequestBody => ({
  data: {
    type: 'view',
    attributes: {
      title: title,
      description: null,
      kind: 'plan',
      default_timescale: defaultTimescale ?? 'month',
      start_at: formatISO(startDate, { representation: 'date' }),
      end_at: formatISO(endDate, { representation: 'date' }),
      start_month_index: 0,
      start_weekday_index: startWeekdayIndex ?? 0,
    },
    relationships: {
      parent_block: parentBlockId
        ? {
            data: {
              id: parentBlockId,
              type: 'block',
            },
          }
        : undefined,
      owner: {
        data: {
          id: creator.uuid,
          type: 'user',
        },
      },
      workspace: {
        data: {
          id: workspaceUuid,
          type: 'workspace',
        },
      },
    },
  },
})

export const useCreateView = ({
  onMutate,
  onSuccess,
  include,
}: QueryParams) => {
  return useMutation({
    mutationFn: (params: CreateViewMutationParams) => {
      return views.create({
        params: { query: { include } },
        requestBody: buildRequestBody(params),
      })
    },
    onMutate,
    onSuccess,
  })
}
